import type { ProjectId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import {
  apartmentMeasureUnit,
  apartmentReferenceKey,
  ownQuantity,
  type ApartmentAverage,
  type ApartmentItem,
  type ApartmentOutOfAverage,
  type ApartmentSteelRate,
} from './apartment-average';
import { serviceElement, type AppliesTo } from './applies-to';
import type { StructuralElementType } from './element-type';
import {
  ownSteelRatesForAverage,
  type FoundationPieceLine,
  type FoundationPieceOutReason,
  type FoundationPiecesOut,
} from './foundation-average';
import {
  foundationPieceSteel,
  stairAndRetainingPieces,
  type StructurePieceElement,
} from './foundation-factors';
import type { ElementGeometry, MeasureBy } from './geometry';
import { importedPieceKind, type ImportedPieceKind } from './imported-piece';
import { ownFactorKey, type ItemFactor } from './own-factor';
import { nomeDaPeca } from './planilha-por-peca';
import { serviceLockKey, serviceLockKeyOf } from './project-budget';
import { referenceItemFor, type ReferenceItem } from './rate-average';
import type { RetainingColumn, RetainingWall } from './retaining';
import type { Stair } from './stair';
import { STEEL_GAUGES, steelRowsFor, type SteelGauge, type SteelRate } from './steel';
import type { StructuralElement, StructuralElementKind } from './structural-element';
import { elementSteelRates } from './structure-factors';

/**
 * A média da estrutura: pilar, viga, escada, muro e pilar de contenção, e o arranque da planilha
 * (Fase 42, etapa 4 — G8, G9, G12, G13; RN-576 a RN-580; ADR-190).
 *
 * É a conta da fundação (`foundation-average.ts`) com outras peças. Uma média só por serviço ×
 * item, para todas as peças do elemento (G9), de duas fontes (G8):
 *
 * - **a peça desenhada:** a medida dela na planta × o fator que vale nela (o próprio do card, o do
 *   tipo de pilar ou de viga, ou o índice da obra) × (1 + perda), **pesando pelas vezes que o
 *   desenho dela se repete na obra** (G12) — a tipologia pelas unidades, o pavimento pelas vezes
 *   dele. O número que veio da média não é de ninguém (D5): nem o índice da obra marcado "da
 *   média", nem a linha do tipo que nasceu da média (ADR-191);
 * - **a peça da planilha por peça:** a quantidade do item ÷ a do serviço na mesma peça. A peça sem
 *   a quantidade do serviço fica fora daquele serviço, e a aba diz quantas (RN-555, RN-578). O
 *   arranque entra assim: ele é reconhecido pelo tipo da peça (`importedPieceKind`), e não pelo
 *   "aplica-se" do serviço, que na Veneza é `NENHUM` (G13).
 *
 * A medida da peça é conta do servidor (ADR-190), com as funções da tela: o pilar e a viga pela
 * geometria do desenho (`structureElementPieces`), a escada e a contenção pela conta delas. Quem
 * chama entrega as peças já medidas, com o fator e a taxa que valem em cada uma.
 *
 * O aço vai à parte, por elemento e bitola (RN-580): Σ (taxa × volume) ÷ Σ volume, com o muro e o
 * pilar de contenção separados; na peça importada, o kg da bitola ÷ o m³ da CONCRETAGEM dela
 * (RN-557). O arranque não tem volume e não entra no aço.
 *
 * Função pura sobre o que o repositório leu.
 */

/**
 * Os elementos desenhados da média da estrutura (RN-576): o pilar e a viga, e a escada e a
 * contenção, as peças da etapa 3. A laje segue com o apartamento.
 */
export type StructureElement = StructuralElementKind | StructurePieceElement;

const DA_ESTRUTURA: ReadonlySet<string> = new Set<StructureElement>([
  'PILAR',
  'VIGA',
  'ESCADA',
  'MURO_CONTENCAO',
  'PILAR_CONTENCAO',
]);

/** O elemento da estrutura que o serviço mede, ou `undefined`. */
export function structureElementOf(service: {
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
}): StructureElement | undefined {
  const elemento = serviceElement(service);
  return elemento !== undefined && DA_ESTRUTURA.has(elemento)
    ? (elemento as StructureElement)
    : undefined;
}

/**
 * O elemento da peça da planilha por peça: o arranque (G13), o muro e o pilar de contenção. As
 * outras (estaca, bloco, baldrame) são da fundação; o pilar e a viga nunca vêm da planilha.
 */
export type StructureImportedElement = 'ARRANQUE' | 'MURO_CONTENCAO' | 'PILAR_CONTENCAO';

const DO_TIPO_IMPORTADO: Readonly<Partial<Record<ImportedPieceKind, StructureImportedElement>>> = {
  ARRANQUE: 'ARRANQUE',
  MURO: 'MURO_CONTENCAO',
  PILAR_CONTENCAO: 'PILAR_CONTENCAO',
};

/**
 * O elemento de uma peça da planilha. O arranque, pelo tipo da peça (`importedPieceKind`): o
 * "aplica-se" dos serviços dele é `NENHUM` (G13). A contenção, pelo "aplica-se" dos serviços da
 * peça, que diz qual dos dois ela é: a disciplina PILARES DE CONTENÇÃO tem os serviços do muro e
 * os do pilar, e o tipo pela disciplina daria pilar ao muro (RN-428). Sem serviço que diga, vale o
 * tipo. A estaca, o bloco e o baldrame ficam fora.
 */
function elementoImportado(
  kind: ImportedPieceKind,
  elementos: ReadonlyMap<StructureElement, number>,
): StructureImportedElement | undefined {
  if (kind === 'ARRANQUE') return 'ARRANQUE';
  if (kind !== 'MURO' && kind !== 'PILAR_CONTENCAO' && kind !== 'OUTRA') return undefined;
  const muro = elementos.get('MURO_CONTENCAO') ?? 0;
  const doPilar = elementos.get('PILAR_CONTENCAO') ?? 0;
  if (muro === 0 && doPilar === 0) return DO_TIPO_IMPORTADO[kind];
  return muro >= doPilar ? 'MURO_CONTENCAO' : 'PILAR_CONTENCAO';
}

/** Uma peça desenhada da estrutura, já medida. */
export interface StructurePiece {
  readonly pieceId: string;
  readonly element: StructureElement;
  /** O nome, para a peça da planilha de mesmo nome e elemento valer por ela (F10). */
  readonly name?: string;
  /** A medida da peça em cada grandeza que o elemento mede. */
  readonly measures: Readonly<Partial<Record<MeasureBy, number>>>;
  /**
   * O fator que vale na peça, item a item: o próprio do card por cima da composição do tipo, cada
   * linha com a origem dela (ADR-191). O item que nenhum dos dois tem usa o índice da obra.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço que vale na peça, quando não é a da disciplina: a própria; sem ela, a do tipo
   * (G6). A lista vale inteira, e cada linha tem a origem dela. Ausente: a da disciplina.
   */
  readonly steelRates?: readonly SteelRate[];
}

/** Um desenho de uma obra marcada: a tipologia ou o pavimento, com as peças da estrutura dele. */
export interface StructureDrawing {
  readonly projectId: ProjectId;
  /**
   * A tipologia do desenho, que casa a peça desenhada com a da planilha de mesmo nome (F10).
   * Ausente no pavimento: a planilha por peça é por tipologia.
   */
  readonly unitTypeId?: string;
  /** As vezes que o desenho se repete na obra (G12). Zero: não entra. */
  readonly times: number;
  readonly pieces: readonly StructurePiece[];
}

/** Uma linha da planilha por peça (`piece_quantities`), com as vezes que a tipologia se repete. */
export interface StructurePieceLine extends FoundationPieceLine {
  /** As vezes que a tipologia da peça se repete na obra (G12). Ausente: uma. */
  readonly times?: number;
}

export interface StructureInput {
  readonly drawings: readonly StructureDrawing[];
  /** Os itens vivos dos serviços das obras marcadas (os mesmos do apartamento e da fundação). */
  readonly items: readonly ApartmentItem[];
  /** A taxa de aço das disciplinas das obras marcadas, com a origem e o elemento (RN-573). */
  readonly steelRates: readonly ApartmentSteelRate[];
  /** As linhas da planilha por peça das obras marcadas; as que não são da estrutura ficam fora. */
  readonly pieceLines: readonly StructurePieceLine[];
}

/** A média da taxa de uma bitola no aço de um elemento da estrutura (RN-580). */
export interface StructureSteelAverage {
  readonly element: StructureElement;
  readonly gauge: SteelGauge;
  /** kg/m³. */
  readonly rate: number;
  /** Σ volume × vezes, em m³. */
  readonly volume: number;
  readonly projects: number;
  /** Quantas peças usam a bitola. */
  readonly pieces: number;
}

export interface StructureAverages {
  /** Por serviço × item; `unitTypes` aqui é quantas peças entraram, como na fundação. */
  readonly averages: readonly ApartmentAverage[];
  readonly outOfAverage: readonly ApartmentOutOfAverage[];
  readonly steel: readonly StructureSteelAverage[];
  /** Quantas peças (desenhadas e importadas) as obras marcadas têm — zero é "sem estrutura". */
  readonly pieces: number;
  /** As peças da planilha que ficaram fora por falta da medida, por motivo × serviço (RN-555). */
  readonly piecesOut: readonly FoundationPiecesOut[];
}

export const NO_STRUCTURE_AVERAGES: StructureAverages = {
  averages: [],
  outOfAverage: [],
  steel: [],
  pieces: 0,
  piecesOut: [],
};

/**
 * O pilar e a viga vivos de um desenho, como a média os lê (ADR-190): a medida de cada um, a da
 * conta (`ElementGeometry.ownMeasures`); o fator que vale nele, item a item — o próprio do card
 * por cima da composição do tipo do mesmo elemento, vivo —, com a origem de cada linha; e a taxa
 * que vale nele (`elementSteelRates`). A peça que a geometria não mediu fica de fora, como no
 * total (`structurePieces`).
 */
export function structureElementPieces(input: {
  readonly elements: readonly StructuralElement[];
  readonly elementTypes: readonly StructuralElementType[];
  /** A geometria do desenho (`computeGeometry(...).elements`). */
  readonly geometry: readonly ElementGeometry[];
}): StructurePiece[] {
  const tipos = new Map(input.elementTypes.map((tipo) => [String(tipo.id), tipo]));
  const medidas = new Map(input.geometry.map((medida) => [String(medida.elementId), medida]));
  return input.elements.flatMap((element): StructurePiece[] => {
    if (element.deletedAt !== undefined) return [];
    const medida = medidas.get(String(element.id));
    if (medida === undefined) return [];
    const tipo =
      element.elementTypeId === undefined ? undefined : tipos.get(String(element.elementTypeId));
    const doTipo =
      tipo === undefined || tipo.deletedAt !== undefined || tipo.kind !== element.kind
        ? []
        : tipo.rates;
    const porChave = new Map<string, ItemFactor>();
    for (const linha of [...doTipo, ...(element.factors ?? [])]) {
      porChave.set(ownFactorKey(linha.serviceKey, linha.itemKey), linha);
    }
    const taxa = elementSteelRates(element, tipos);
    return [
      {
        pieceId: element.id,
        element: element.kind,
        ...(element.name === undefined ? {} : { name: element.name }),
        measures: medida.ownMeasures,
        ...(porChave.size === 0 ? {} : { factors: [...porChave.values()] }),
        ...(taxa === undefined ? {} : { steelRates: taxa }),
      },
    ];
  });
}

/**
 * A escada, o muro e o pilar de contenção vivos de uma tipologia, como a média os lê (ADR-190): a
 * medida de cada um, a da conta da tela (`stairAndRetainingPieces` — o muro desconta os pilares
 * encaixados, o pilar perde as faces dentro do muro); o nome; o fator próprio do card, com a
 * origem de cada linha; e a taxa própria. Sem tipo (G3).
 */
export function structureStairAndRetainingPieces(input: {
  readonly stairs?: readonly Stair[];
  readonly retainingWalls?: readonly RetainingWall[];
  readonly retainingColumns?: readonly RetainingColumn[];
}): StructurePiece[] {
  const daConta = new Map(
    stairAndRetainingPieces(input).pieces.map((peca) => [String(peca.pieceId), peca]),
  );
  const pecas: readonly {
    readonly id: string;
    readonly name?: string;
    readonly factors?: readonly ItemFactor[];
  }[] = [
    ...(input.stairs ?? []),
    ...(input.retainingWalls ?? []),
    ...(input.retainingColumns ?? []),
  ];
  return pecas.flatMap((peca): StructurePiece[] => {
    const medida = daConta.get(String(peca.id));
    if (medida === undefined) return [];
    const nome = peca.name?.trim();
    return [
      {
        pieceId: medida.pieceId,
        element: medida.element,
        ...(nome === undefined || nome === '' ? {} : { name: nome }),
        measures: medida.measures,
        ...(peca.factors === undefined || peca.factors.length === 0
          ? {}
          : { factors: peca.factors }),
        ...(medida.steelRates === undefined ? {} : { steelRates: medida.steelRates }),
      },
    ];
  });
}

interface Acumulado {
  readonly serviceKey: string;
  readonly item: ReferenceItem;
  readonly unit: string;
  readonly ordem: number;
  quantity: number;
  measure: number;
  readonly projects: Set<string>;
  readonly pieces: Set<string>;
}

/** Um número próprio de um item: a quantidade e a medida de uma peça, já vezes o desenho. */
interface Numero {
  readonly projectId: ProjectId;
  readonly piece: string;
  readonly quantity: number;
  readonly measure: number;
}

/** Uma peça desenhada que entra na média, com as vezes do desenho e o fator pela chave. */
interface Desenhada {
  readonly projectId: ProjectId;
  readonly times: number;
  readonly piece: StructurePiece;
  readonly factors: ReadonlyMap<string, ItemFactor>;
}

/** O que as linhas de uma peça da planilha dizem dela: as disciplinas e os elementos. */
interface DaPeca {
  readonly disciplinas: string[];
  readonly elementos: Map<StructureElement, number>;
}

/** As peças da planilha que ficaram fora de um serviço, por motivo (RN-555). */
interface Fora {
  readonly reason: FoundationPieceOutReason;
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly pieces: Set<string>;
  readonly projects: Set<string>;
}

type AnotarFora = (
  reason: FoundationPieceOutReason,
  linha: StructurePieceLine,
  peca: string,
) => void;

const DA_DISCIPLINA = (projectId: ProjectId, disciplina: string) =>
  `${projectId}|${normalizeName(disciplina)}`;

/** A peça da planilha por peça: a mesma obra, tipologia e nome (`nomeDaPeca`). */
const PECA_DA_LINHA = (linha: StructurePieceLine) =>
  `${linha.projectId}|${linha.unitTypeId}|${nomeDaPeca(linha.pieceName)}`;

const VEZES = (linha: StructurePieceLine) => linha.times ?? 1;

/** O m³ da peça importada é o da CONCRETAGEM dela (RN-557), como na fundação. */
const DA_CONCRETAGEM = `|${normalizeName('CONCRETAGEM')}`;
const ehConcretagem = (linha: StructurePieceLine): boolean =>
  serviceLockKey(linha.serviceKey).endsWith(DA_CONCRETAGEM);

/**
 * A média da estrutura das obras marcadas (RN-577 a RN-580), com o que ficou fora (RN-555). Sem
 * referência, nada (D3).
 */
export function averageStructureRates(
  input: StructureInput,
  reference: readonly ReferenceItem[] | undefined,
): StructureAverages {
  if (reference === undefined) return NO_STRUCTURE_AVERAGES;

  // O elemento de cada peça da planilha, pelas disciplinas e pelo "aplica-se" dos serviços dela:
  // só o arranque, o muro e o pilar de contenção são da estrutura.
  const daPeca = new Map<string, DaPeca>();
  for (const linha of input.pieceLines) {
    const peca = PECA_DA_LINHA(linha);
    const anotada: DaPeca = daPeca.get(peca) ?? {
      disciplinas: [],
      elementos: new Map<StructureElement, number>(),
    };
    anotada.disciplinas.push(linha.disciplineName);
    const elemento = structureElementOf(linha);
    if (elemento !== undefined) {
      anotada.elementos.set(elemento, (anotada.elementos.get(elemento) ?? 0) + 1);
    }
    daPeca.set(peca, anotada);
  }
  const elementoDaPeca = new Map<string, StructureImportedElement>();
  for (const [peca, { disciplinas, elementos }] of daPeca) {
    const elemento = elementoImportado(importedPieceKind(disciplinas), elementos);
    if (elemento !== undefined) elementoDaPeca.set(peca, elemento);
  }
  const linhas = input.pieceLines.filter((linha) => elementoDaPeca.has(PECA_DA_LINHA(linha)));

  // A peça da planilha vale pelo número dela (F10): a desenhada de mesmo nome e elemento, na
  // mesma tipologia, sai. A escada, o pilar e a viga nunca casam: a planilha não os traz.
  const importadas = new Set([...elementoDaPeca].map(([peca, elemento]) => `${peca}|${elemento}`));
  const desenhadas: Desenhada[] = [];
  for (const desenho of input.drawings) {
    if (!(desenho.times > 0)) continue;
    for (const peca of desenho.pieces) {
      // A lista vazia é "nenhuma": a peça usa a da disciplina, como na conta (`cheia`).
      const { steelRates, ...semTaxa } = peca;
      const piece: StructurePiece =
        steelRates !== undefined && steelRates.length > 0 ? peca : semTaxa;
      const daPlanilha =
        desenho.unitTypeId !== undefined &&
        piece.name !== undefined &&
        importadas.has(
          `${desenho.projectId}|${desenho.unitTypeId}|${nomeDaPeca(piece.name)}|${piece.element}`,
        );
      if (daPlanilha) continue;
      desenhadas.push({
        projectId: desenho.projectId,
        times: desenho.times,
        piece,
        factors: new Map(
          (piece.factors ?? []).map((linha) => [
            ownFactorKey(linha.serviceKey, linha.itemKey),
            linha,
          ]),
        ),
      });
    }
  }
  // A viga da divisa entra no desenho de cada tipologia que ela toca, com a parte dela (RN-163):
  // a peça é uma só, e conta uma vez.
  const pecas =
    new Set(desenhadas.map(({ projectId, piece }) => `${projectId}|${piece.pieceId}`)).size +
    elementoDaPeca.size;

  // O que ficou fora por falta da medida na planilha (RN-555): por motivo × serviço, as peças.
  const foraDasPecas = new Map<string, Fora>();
  const anotarFora: AnotarFora = (reason, linha, peca) => {
    const servico = serviceLockKey(linha.serviceKey);
    const chave = `${reason}|${servico}`;
    const anotado = foraDasPecas.get(chave) ?? {
      reason,
      serviceKey: servico,
      disciplineName: linha.disciplineName,
      serviceName: linha.serviceName,
      pieces: new Set<string>(),
      projects: new Set<string>(),
    };
    anotado.pieces.add(peca);
    anotado.projects.add(linha.projectId);
    foraDasPecas.set(chave, anotado);
  };

  // A taxa da disciplina de cada obra, para o aço medido da peça sem taxa própria nem de tipo.
  const taxasDaDisciplina = new Map<string, ApartmentSteelRate[]>();
  for (const taxa of input.steelRates) {
    const chave = `${taxa.projectId}|${taxa.disciplineKey}`;
    taxasDaDisciplina.set(chave, [...(taxasDaDisciplina.get(chave) ?? []), taxa]);
  }

  // As linhas por peça: a quantidade do serviço e a de cada item, por obra × tipologia × peça.
  const doServico = new Map<string, number>();
  const doItem = new Map<string, StructurePieceLine[]>();
  for (const linha of linhas) {
    if (!(linha.quantity > 0)) continue;
    if (linha.itemId === undefined) {
      doServico.set(`${PECA_DA_LINHA(linha)}|${linha.serviceKey}`, linha.quantity);
    } else {
      doItem.set(linha.itemId, [...(doItem.get(linha.itemId) ?? []), linha]);
    }
  }

  const daReferencia = new Map<string, ReferenceItem[]>();
  reference.forEach((item) => {
    const chave = serviceLockKeyOf(item.disciplineName, item.serviceName);
    daReferencia.set(chave, [...(daReferencia.get(chave) ?? []), item]);
  });
  const ordemDaReferencia = new Map(reference.map((item, indice) => [item, indice]));

  const acumulados = new Map<string, Acumulado>();
  const fora = new Map<string, ApartmentOutOfAverage>();

  for (const item of input.items) {
    // A bitola vai pela taxa, à parte (RN-580).
    if (item.steelGauge !== undefined) continue;
    const elemento = structureElementOf(item);
    const daPlanilha = doItem.get(item.id) ?? [];
    if (elemento === undefined && daPlanilha.length === 0) continue;
    const numeros: Numero[] = [];

    // A peça desenhada (RN-577), pesando pelas vezes do desenho (G12).
    if (elemento !== undefined) {
      const chave = ownFactorKey(item.serviceKey, normalizeName(item.description));
      // A lista do elemento, na disciplina que mistura (RN-573).
      const disciplina = steelRowsFor(
        taxasDaDisciplina.get(DA_DISCIPLINA(item.projectId, item.disciplineName)) ?? [],
        elemento,
      );
      for (const desenhada of desenhadas) {
        if (desenhada.projectId !== item.projectId || desenhada.piece.element !== elemento) {
          continue;
        }
        const medida = medidaDaPeca(desenhada.piece, item.measureBy, disciplina);
        if (medida === undefined || !(medida > 0)) continue;
        const fator = fatorQueVale(desenhada.factors.get(chave), item);
        if (fator === undefined) continue;
        const quantidade = ownQuantity(
          {
            kind: item.kind,
            consumptionIndex: fator,
            ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
          },
          medida,
          undefined,
        );
        if (quantidade === undefined) continue;
        numeros.push({
          projectId: item.projectId,
          piece: desenhada.piece.pieceId,
          quantity: quantidade * desenhada.times,
          measure: medida * desenhada.times,
        });
      }
    }

    // A peça da planilha por peça (RN-578): o item ÷ o serviço na mesma peça. Sem a quantidade
    // do serviço — a fôrma e a montagem do arranque na Veneza (G13) —, ela fica fora.
    for (const linha of daPlanilha) {
      const peca = PECA_DA_LINHA(linha);
      const medida = doServico.get(`${peca}|${linha.serviceKey}`);
      if (medida === undefined || !(medida > 0)) {
        anotarFora('SEM_QUANTIDADE_DO_SERVICO', linha, peca);
        continue;
      }
      numeros.push({
        projectId: item.projectId,
        piece: peca,
        quantity: linha.quantity * VEZES(linha),
        measure: medida * VEZES(linha),
      });
    }
    if (numeros.length === 0) continue;

    const servico = serviceLockKey(item.serviceKey);
    const par = referenceItemFor(item, daReferencia.get(servico) ?? []);
    if (par === undefined) {
      const chaveFora = `${item.projectId}|${servico}|${normalizeName(item.description)}`;
      const antes = fora.get(chaveFora);
      fora.set(chaveFora, {
        reason: 'ITEM_SEM_PAR',
        projectId: item.projectId,
        serviceName: item.serviceName,
        description: item.description,
        unitTypes: (antes?.unitTypes ?? 0) + numeros.length,
      });
      continue;
    }
    const chaveDaMedia = `${servico}|${apartmentReferenceKey(par)}`;
    const acumulado = acumulados.get(chaveDaMedia) ?? {
      serviceKey: servico,
      item: par,
      unit: apartmentMeasureUnit({
        measureBy: item.measureBy,
        ...(item.appliesTo === undefined ? {} : { appliesTo: item.appliesTo }),
        ...(item.serviceUnit === undefined ? {} : { unit: item.serviceUnit }),
      }),
      ordem: ordemDaReferencia.get(par) ?? 0,
      quantity: 0,
      measure: 0,
      projects: new Set<string>(),
      pieces: new Set<string>(),
    };
    for (const numero of numeros) {
      acumulado.quantity += numero.quantity;
      acumulado.measure += numero.measure;
      acumulado.projects.add(numero.projectId);
      acumulado.pieces.add(`${numero.projectId}|${numero.piece}`);
    }
    acumulados.set(chaveDaMedia, acumulado);
  }

  const averages = [...acumulados.values()]
    .sort((a, b) => a.ordem - b.ordem)
    .map((a): ApartmentAverage => ({
      serviceKey: a.serviceKey,
      disciplineName: a.item.disciplineName,
      serviceName: a.item.serviceName,
      item: a.item,
      measureUnit: a.unit,
      rate: a.quantity / a.measure,
      quantity: a.quantity,
      measure: a.measure,
      projects: a.projects.size,
      unitTypes: a.pieces.size,
    }));

  // O aço anota antes a peça sem a concretagem, e o "fora" sai com os dois motivos.
  const steel = mediaDoAco(
    input.items,
    desenhadas,
    taxasDaDisciplina,
    linhas,
    elementoDaPeca,
    anotarFora,
  );
  return {
    averages,
    outOfAverage: [...fora.values()],
    steel,
    pieces: pecas,
    piecesOut: [...foraDasPecas.values()].map((anotado): FoundationPiecesOut => ({
      reason: anotado.reason,
      serviceKey: anotado.serviceKey,
      disciplineName: anotado.disciplineName,
      serviceName: anotado.serviceName,
      pieces: anotado.pieces.size,
      projects: anotado.projects.size,
    })),
  };
}

/**
 * O fator que vale num item de uma peça: o da linha da peça (o próprio do card, ou o do tipo);
 * sem ela, o índice da obra. O que veio da média não é número de ninguém (D5): a linha "da média"
 * vale na peça, mas deixa a peça fora — não cai no índice, que não é o que vale nela.
 */
function fatorQueVale(
  linha: ItemFactor | undefined,
  item: Pick<ApartmentItem, 'consumptionIndex' | 'indexOrigin'>,
): number | undefined {
  if (linha !== undefined) return linha.origin === 'MEDIA' ? undefined : linha.factor;
  return item.indexOrigin === 'MEDIA' ? undefined : item.consumptionIndex;
}

/**
 * A medida de uma peça na grandeza do serviço. No serviço de aço, os kg da peça: o volume × a
 * soma das bitolas da taxa que vale nela (a própria ou a do tipo; sem elas, a da disciplina) — a
 * mesma conta da parte da peça na tela (`foundationPieceSteel`, RN-556).
 */
function medidaDaPeca(
  peca: StructurePiece,
  grandeza: MeasureBy,
  disciplina: readonly SteelRate[] | undefined,
): number | undefined {
  if (grandeza !== 'ACO') return peca.measures[grandeza];
  const kg = foundationPieceSteel(peca, disciplina);
  return kg > 0 ? kg : undefined;
}

/**
 * A média do aço por elemento e bitola (RN-580): Σ (taxa × volume × vezes) ÷ Σ (volume × vezes).
 * A peça desenhada usa a taxa que vale nela — a própria, a do tipo, ou a da disciplina (a lista do
 * elemento, RN-573). A peça importada, o kg da bitola ÷ o m³ da CONCRETAGEM dela (RN-557); sem a
 * concretagem, ela fica fora do aço (RN-555). O arranque não tem volume: não entra.
 */
function mediaDoAco(
  items: readonly ApartmentItem[],
  desenhadas: readonly Desenhada[],
  taxasDaDisciplina: ReadonlyMap<string, readonly ApartmentSteelRate[]>,
  linhas: readonly StructurePieceLine[],
  elementoDaPeca: ReadonlyMap<string, StructureImportedElement>,
  anotarFora: AnotarFora,
): readonly StructureSteelAverage[] {
  const acumulado = new Map<
    string,
    { kg: number; volume: number; projects: Set<string>; pieces: Set<string> }
  >();
  const somar = (
    elemento: StructureElement,
    projectId: ProjectId,
    peca: string,
    volume: number,
    taxa: (gauge: SteelGauge) => number | undefined,
  ) => {
    for (const { code } of STEEL_GAUGES) {
      const kg = taxa(code);
      if (kg === undefined) continue;
      const chave = `${elemento}|${code}`;
      const linha = acumulado.get(chave) ?? {
        kg: 0,
        volume: 0,
        projects: new Set<string>(),
        pieces: new Set<string>(),
      };
      linha.kg += kg * volume;
      linha.volume += volume;
      if (kg > 0) {
        linha.projects.add(projectId);
        linha.pieces.add(peca);
      }
      acumulado.set(chave, linha);
    }
  };

  // A disciplina do aço de cada elemento, em cada obra: a do serviço de aço, quando há item nele;
  // senão, a de qualquer serviço do elemento.
  const disciplinaDoAco = new Map<string, string>();
  for (const item of items) {
    const elemento = structureElementOf(item);
    if (elemento === undefined) continue;
    const chave = `${item.projectId}|${elemento}`;
    if (item.measureBy === 'ACO' || !disciplinaDoAco.has(chave)) {
      disciplinaDoAco.set(chave, normalizeName(item.disciplineName));
    }
  }

  // A peça desenhada, pesando pelas vezes do desenho (G12).
  for (const { projectId, times, piece } of desenhadas) {
    const volume = (piece.measures.VOLUME ?? 0) * times;
    if (!(volume > 0)) continue;
    const disciplina = disciplinaDoAco.get(`${projectId}|${piece.element}`);
    const lista =
      piece.steelRates ??
      steelRowsFor(
        disciplina === undefined ? [] : (taxasDaDisciplina.get(`${projectId}|${disciplina}`) ?? []),
        piece.element,
      );
    const taxa = ownSteelRatesForAverage(lista);
    if (taxa === undefined) continue;
    somar(piece.element, projectId, `${projectId}|${piece.pieceId}`, volume, taxa);
  }

  // A peça da planilha por peça: o m³ da CONCRETAGEM (RN-557) e o aço por bitola. O maior número
  // protege da linha repetida; o aço é o do serviço que mais pesa (`pieceMeasures`, RN-366).
  const porPeca = new Map<
    string,
    {
      projectId: ProjectId;
      elemento: StructureElement;
      volume: number;
      vezes: number;
      kgPorServico: Map<string, Map<SteelGauge, number>>;
      doAco: StructurePieceLine[];
    }
  >();
  for (const linha of linhas) {
    if (!(linha.quantity > 0)) continue;
    const peca = PECA_DA_LINHA(linha);
    const elemento = elementoDaPeca.get(peca);
    if (elemento === undefined || elemento === 'ARRANQUE') continue;
    const daPeca = porPeca.get(peca) ?? {
      projectId: linha.projectId,
      elemento,
      volume: 0,
      vezes: VEZES(linha),
      kgPorServico: new Map<string, Map<SteelGauge, number>>(),
      doAco: [],
    };
    if (linha.itemId === undefined && ehConcretagem(linha)) {
      daPeca.volume = Math.max(daPeca.volume, linha.quantity);
    } else if (linha.itemId !== undefined && linha.steelGauge !== undefined) {
      const doServico = daPeca.kgPorServico.get(linha.serviceKey) ?? new Map<SteelGauge, number>();
      doServico.set(linha.steelGauge, (doServico.get(linha.steelGauge) ?? 0) + linha.quantity);
      daPeca.kgPorServico.set(linha.serviceKey, doServico);
      daPeca.doAco.push(linha);
    }
    porPeca.set(peca, daPeca);
  }
  const somaDe = (kg: ReadonlyMap<SteelGauge, number>): number =>
    [...kg.values()].reduce((soma, valor) => soma + valor, 0);
  for (const [peca, daPeca] of porPeca) {
    const kg = [...daPeca.kgPorServico.values()].reduce<Map<SteelGauge, number> | undefined>(
      (maior, doServico) =>
        maior === undefined || somaDe(doServico) > somaDe(maior) ? doServico : maior,
      undefined,
    );
    if (kg === undefined) continue;
    if (!(daPeca.volume > 0)) {
      daPeca.doAco.forEach((linha) => anotarFora('SEM_CONCRETAGEM', linha, peca));
      continue;
    }
    somar(
      daPeca.elemento,
      daPeca.projectId,
      peca,
      daPeca.volume * daPeca.vezes,
      (gauge) => (kg.get(gauge) ?? 0) / daPeca.volume,
    );
  }

  return [...acumulado.entries()].flatMap(([chave, linha]): StructureSteelAverage[] => {
    if (!(linha.kg > 0) || !(linha.volume > 0)) return [];
    const [element, gauge] = chave.split('|') as [StructureElement, SteelGauge];
    return [
      {
        element,
        gauge,
        rate: linha.kg / linha.volume,
        volume: linha.volume,
        projects: linha.projects.size,
        pieces: linha.pieces.size,
      },
    ];
  });
}
